import {
  MISSING_OUTBOUND_LABEL,
  UNVERIFIED_OUTBOUND_LABEL,
  withOutboundCarrierGates,
} from '@/utils/fulfillmentOutboundGate';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { v4 as uuidv4 } from 'uuid';
import { FulfillmentAdminClient } from '@/infrastructure/FulfillmentAdminClient';
import { listEveryOpenRound } from '@/pages/fulfillment/components/openRounds';
import { OperatorIdentity } from '@/infrastructure/OperatorIdentity';
import type {
  BulkOutboundResponse,
  ListReadyToOutboundParams,
  OrderComment,
  ReadyToOutboundGroup,
  ReadyToOutboundTotals,
} from '@/infrastructure/FulfillmentAdminClient';
import { getAdminApiUrl, AdminApiDomain } from '@/config/ApiRouting';
import { ROUTES } from '@/routes/routes';
import { CarrierLabelModal } from './components/CarrierLabelModal';
import { isAxiosError } from 'axios';
import { formatBulkResult, UNCONFIRMED_BULK_RESULT } from '@/pages/fulfillment/components/bulkResult';
import type { FulfillmentToastState } from '@/pages/fulfillment/components/useFulfillmentToast';
import { PickListSheet } from '@/pages/fulfillment/components/PickListSheet';
import { pickerLocation } from '@/pages/fulfillment/components/lineLocations';
import { RowComments } from './components/RowComments';
import { FulfillmentSound } from '@/pages/fulfillment/components/outboundSound';
import { PrintDispatcher, PrintFailureNotice } from '@/pages/fulfillment/components/PrintDispatcher';
import {
  archiveHasNothing,
  packingNotPrintedNote,
  printPaperwork,
  printParcels,
  sheetOutcomesOf,
  type ArchivedParcel,
  type Parcel,
  type ParcelPrintOutcome,
} from '@/pages/fulfillment/components/parcelPaperwork';
import { CheckCell } from '@/pages/fulfillment/components/CheckCell';
import { FulfillmentToastHost } from '@/pages/fulfillment/components/FulfillmentToast';
import { AUTO_DISMISS, useFulfillmentToast } from '@/pages/fulfillment/components/useFulfillmentToast';
import { describeFailure, violationBadges } from '@/utils/fulfillmentErrors';
import type { PrintFailure, PrintOutcome } from '@/models/FulfillmentPrint';
import {
  NOT_OUTBOUNDED,
  NO_CARRIER_LABEL,
  drawnLabelOf,
  drawnLabelRefusalOf,
  labelRunOutcome,
  opensLabelSheet,
  trackingNumberOf,
} from './carrierLabel';
import type { CarrierLabelBatch } from './carrierLabel';
import {
  countsByView,
  formatOrderDate,
  groupedByView,
  inView,
  MISSING,
  outboundableSplit,
  selectedUnits,
  unitsOf,
} from './readyToOutboundView';
import type { ReadyToOutboundRow, ReadyToOutboundTab } from './readyToOutboundView';
import { BoxIcon, CommentIcon, PrintIcon, RefreshIcon } from '@/pages/fulfillment/components/UltimateIcons';
import '../wireframe.css';
import '../ultimateDesign.css';
import './readyToOutbound.css';

/** One bulk run: the three buttons share a bar, so they share a shape too. */
interface RunState {
  label: string;
  done: number;
  total: number;
  mode: string;
  /** The notification that reported how the run ended; the bar stays for as long as that one does. */
  outcomeToastId?: number;
}

/** Which thread read or post may set a row's comment count when it answers. */
interface CommentCountClaim {
  orderId: number;
  revision: number;
  generation: number;
}

const PAGE_SIZE = 50;

const GENERIC_ERROR = 'The request failed. Please try again shortly.';

/** Print keeps the picker where they were; an outbound empties the queue underneath them. */
/** A picker away this long may come back to a moved queue; a shorter hop (a glance elsewhere) does not re-read it. */
const AWAY_RELOAD_MS = 30_000;

const plural = (count: number, word: string): string => `${count} ${word}${count === 1 ? '' : 's'}`;

const KEEP_SELECTION = 'No refresh · selection kept';
const RELOADS = 'refreshes after completion';

const TABS: Array<{ key: ReadyToOutboundTab; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'inv', label: 'Inventory' },
  { key: 'mkt', label: 'Marketing' },
  { key: 'jit', label: 'JIT' },
];

/** Why this order is out of the run, in the server's own words rather than a badge's shorthand [T04]. */
const describeExclusion = (violations: string[]): string =>
  violations.length === 0 ? '' : `Cannot be outbounded yet — ${violations.join(' · ')}`;

/** How many orders the run did not ship, which is what decides whether the panel opens itself. */
const left = (result: BulkOutboundResponse): number => result.skipped.length + result.failed.length;

const readCarrierCheckedQueue = async (client: FulfillmentAdminClient, params: ListReadyToOutboundParams) => {
  const result = await client.listReadyToOutbound(params);
  return {
    ...result,
    orders: await withOutboundCarrierGates(result.orders, (id) => client.getOrderDetailForOutbound(id)),
  };
};

export default function FulfillmentReadyToOutboundPage() {
  const client = useMemo(() => new FulfillmentAdminClient(getAdminApiUrl(AdminApiDomain.FULFILLMENT)), []);
  // Who is reading, so each row can carry how many comments they have not seen (SSD-7435): attribution, like a comment's author.
  const operator = useMemo(() => OperatorIdentity.current(), []);

  const [orders, setOrders] = useState<ReadyToOutboundRow[]>([]);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  /** The band the page ended in, so Load more resumes inside it; null from a server that pages by id alone. */
  const [nextCursorGroup, setNextCursorGroup] = useState<ReadyToOutboundGroup | null>(null);
  /** A page was gated without the live WooCommerce read, so a status on screen may be behind the shop. */
  const [liveStatusDegraded, setLiveStatusDegraded] = useState(false);
  /** What the whole queue holds per tab; null on a server that does not count it [T04]. */
  const [totals, setTotals] = useState<ReadyToOutboundTotals | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionBusy, setBusy] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const busy = actionBusy || loadingMore;
  // A run in flight owns the queue: re-reading under it would drop the rows it is still working on [SSD-6939 19-2].
  // Load more is not a run — a reload or tab switch already supersedes it through the queue generation.
  const actionBusyRef = useRef(actionBusy);
  useEffect(() => {
    actionBusyRef.current = actionBusy;
  }, [actionBusy]);
  const [bulkResult, setBulkResult] = useState<BulkOutboundResponse | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [tab, setTab] = useState<ReadyToOutboundTab>('all');
  const [run, setRun] = useState<RunState | null>(null);
  // The sheet keeps the run that opened it, so a sheet closed mid-print reports only while no newer run has started.
  const [labels, setLabels] = useState<{ batch: CarrierLabelBatch; run: number } | null>(null);
  const runCount = useRef(0);
  const [openComments, setOpenComments] = useState<number | null>(null);
  const [comments, setComments] = useState<OrderComment[]>([]);
  const [reloadToken, setReloadToken] = useState(0);
  /** A re-read that keeps the rows on screen and the ticks that still apply, for a picker back from a real absence. */
  const [refreshToken, setRefreshToken] = useState(0);
  /**
   * Shelves a stock audit is counting right now [SSD-7447]. The audit writes each count as it goes, so picking from them
   * is never held; the picker is only told the quantity there may change while they pick.
   */
  const [countingShelves, setCountingShelves] = useState<ReadonlySet<string>>(() => new Set());
  useEffect(() => {
    let ignore = false;
    Promise.resolve()
      // Every page: a full count walk holds more shelves than one page serves [SSD-7662].
      .then(() => listEveryOpenRound((params) => client.listStockTakes(params)))
      .then((rounds) => {
        if (!ignore) {
          setCountingShelves(new Set(rounds.map((round) => round.locationCode)));
        }
      })
      .catch(() => {
        // Without the rounds no shelf is marked; nothing about picking depends on the mark.
      });
    return () => {
      ignore = true;
    };
  }, [client, refreshToken]);
  /** A retry of the same draft is the same write — a fresh key would post it twice. */
  const commentKey = useRef<{ fingerprint: string; idempotencyKey: string } | null>(null);
  const openThreadRef = useRef<number | null>(null);
  const commentInFlightRef = useRef(false);
  const queueGeneration = useRef(0);
  // Bumped by every thread read or post, so only the newest one per order may set that row's count.
  const commentCountRevision = useRef(new Map<number, number>());
  const { toasts, showSuccess, showError, showWarning, dismiss } = useFulfillmentToast();

  /** Every sheet this screen produces goes through the one dispatcher, which prints it at once. */
  const printer = useMemo(() => new PrintDispatcher(), []);

  /**
   * The queue moves under a picker who was away from the tab, so it is re-read on the way back rather
   * than polled — a poll that lands mid-selection takes rows out from under a tick [D-p].
   */
  /** How long the picker has to be away before the queue is re-read on the way back: a short hop elsewhere is not an absence. */
  const hiddenAtRef = useRef<number | null>(null);
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'hidden') {
        hiddenAtRef.current = Date.now();
        return;
      }
      const away = hiddenAtRef.current === null ? Number.POSITIVE_INFINITY : Date.now() - hiddenAtRef.current;
      hiddenAtRef.current = null;
      if (document.visibilityState === 'visible' && !actionBusyRef.current && away >= AWAY_RELOAD_MS) {
        setRefreshToken((token) => token + 1);
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  /**
   * The queue read again under the picker, quietly: the rows stay up until the new ones replace them, and a
   * tick stays on every order the queue still holds. Nothing blinks and nothing is un-ticked [D-p].
   */
  useEffect(() => {
    if (refreshToken === 0) {
      return undefined;
    }
    let ignore = false;
    const generation = queueGeneration.current;
    readCarrierCheckedQueue(client, { limit: PAGE_SIZE, ...(tab !== 'all' && { tab }), ...(operator && { operator }) })
      .then((res) => {
        if (ignore || generation !== queueGeneration.current) {
          return;
        }
        setOrders(res.orders);
        setNextCursor(res.nextCursor);
        setNextCursorGroup(res.nextCursorGroup ?? null);
        setLiveStatusDegraded(res.liveStatusDegraded === true);
        setTotals(res.totals ?? null);
        setSelected((picked) =>
          picked.filter((orderId) =>
            res.orders.some((order) => order.orderId === orderId && order.violations.length === 0),
          ),
        );
      })
      .catch(() => {
        // The rows on screen are still the last good read; a failed quiet refresh is not worth a red card.
      });
    return () => {
      ignore = true;
    };
  }, [client, operator, refreshToken, tab]);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    setLoadingMore(false);
    setOrders([]);
    setSelected([]);
    setNextCursor(null);
    setNextCursorGroup(null);
    setLiveStatusDegraded(false);
    readCarrierCheckedQueue(client, { limit: PAGE_SIZE, ...(tab !== 'all' && { tab }), ...(operator && { operator }) })
      .then((res) => {
        if (ignore) {
          return;
        }
        setOrders(res.orders);
        setNextCursor(res.nextCursor);
        setNextCursorGroup(res.nextCursorGroup ?? null);
        setLiveStatusDegraded(res.liveStatusDegraded === true);
        setTotals(res.totals ?? null);
      })
      .catch(() => {
        if (!ignore) {
          showError(GENERIC_ERROR);
        }
      })
      .finally(() => {
        if (!ignore) {
          setLoading(false);
        }
      });
    return () => {
      ignore = true;
      queueGeneration.current += 1;
    };
  }, [client, operator, reloadToken, showError, tab]);

  /** Closing the run's outcome card takes the bar with it. */
  const dismissToast = (id: number) => {
    dismiss(id);
    // Only the run's own outcome takes the bar with it; an unrelated notice closing must not end a run in progress.
    setRun((prev) => (prev?.outcomeToastId === id ? null : prev));
  };

  /**
   * A success running out leaves the finished bar as the run's paper trail: a picker with their hands in a box
   * looks up late. A refusal or warning that runs out (only a brief one is marked to) takes the bar with it, or
   * an empty run would read as a finished one once its card left (SSD-7705).
   */
  const expireToast = (id: number) => {
    const tone = toasts.find((toast) => toast.id === id)?.tone;
    if (tone === 'success') {
      dismiss(id);
      return;
    }
    dismissToast(id);
  };

  // The tab says how much work is queued; counting the loaded page said how much of it fits on screen.
  const counts = totals ?? countsByView(orders);
  const visible = groupedByView(orders.filter((order) => inView(order, tab)));
  const selectedIds = [...selected].sort((a, b) => a - b);
  const carrierBlocked = visible.filter((order) =>
    order.violations.some(
      (violation) => violation === MISSING_OUTBOUND_LABEL || violation === UNVERIFIED_OUTBOUND_LABEL,
    ),
  );
  const selectedBlocked = visible.some((order) => selected.includes(order.orderId) && order.violations.length > 0);
  // The server refuses an order that still has violations, so the screen never offers it [FS-097].
  const selectable = visible.filter((order) => (order.violations ?? []).length === 0);
  const split = outboundableSplit(visible);
  // Named from the listed rows themselves, so a reason the badge table does not know still reaches the tooltip.
  const blockedReasons = violationBadges(visible.flatMap((order) => order.violations ?? []));
  const allVisibleSelected = selectable.length > 0 && selectable.every((order) => selected.includes(order.orderId));
  const percent = run && run.total > 0 ? Math.round((run.done / run.total) * 100) : 0;
  // Only a success outlives its card, so a settled run whose card is gone ended well (SSD-7778).
  const outcomeTone =
    run?.outcomeToastId === undefined
      ? undefined
      : (toasts.find((toast) => toast.id === run.outcomeToastId)?.tone ?? 'success');
  const runDone = outcomeTone === 'success';
  const progressLabel = !run
    ? ''
    : outcomeTone === undefined
      ? `${run.label} in progress — ${percent}% · ${run.mode} · toast on completion`
      : runDone
        ? `${run.label} done`
        : `${run.label} ${outcomeTone === 'error' ? 'stopped' : 'finished with warnings'} — see the notice · ${run.mode}`;

  const loadMore = async () => {
    if (nextCursor == null || busy || loading) {
      return;
    }
    setLoadingMore(true);
    const generation = queueGeneration.current;
    try {
      const res = await readCarrierCheckedQueue(client, {
        limit: PAGE_SIZE,
        beforeOrderId: nextCursor,
        ...(nextCursorGroup != null && { cursorGroup: nextCursorGroup }),
        ...(tab !== 'all' && { tab }),
        ...(operator && { operator }),
      });
      if (generation !== queueGeneration.current) {
        return;
      }
      setOrders((prev) => [...prev, ...res.orders]);
      setNextCursor(res.nextCursor);
      setNextCursorGroup(res.nextCursorGroup ?? null);
      // Rows already on screen keep whatever the earlier pages said, so one late page marks the whole list.
      if (res.liveStatusDegraded === true) {
        setLiveStatusDegraded(true);
      }
    } catch (err) {
      if (generation === queueGeneration.current) {
        showError(describeFailure(err, GENERIC_ERROR));
      }
    } finally {
      if (generation === queueGeneration.current) {
        setLoadingMore(false);
      }
    }
  };

  const toggle = (orderId: number) =>
    setSelected((prev) => (prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]));

  const toggleAll = () => {
    const ids = selectable.map((order) => order.orderId);
    setSelected((prev) =>
      allVisibleSelected ? prev.filter((id) => !ids.includes(id)) : [...new Set([...prev, ...ids])],
    );
  };

  /** A new run leaves the last one's outcome standing: the picker may not have read it yet. */
  const start = (label: string, total: number, mode: string) => {
    runCount.current += 1;
    setRun({ label, done: 0, total, mode });
  };

  /** The bar fills and is tied to the notification that says how the run ended. */
  const settle = (outcomeToastId: number) =>
    setRun((prev) => (prev ? { ...prev, done: prev.total, outcomeToastId } : prev));

  const finish = (label: string, orderCount: number) => settle(showSuccess(`${label} complete — ${orderCount} orders`));

  /** A label run either produced every sheet or it did not, and the difference is worth the warning tone. */
  const reportLabelRun = (outcome: FulfillmentToastState) => {
    const show = outcome.tone === 'error' ? showError : outcome.tone === 'warning' ? showWarning : showSuccess;
    settle(show(outcome.text, outcome.details));
  };

  /** A newer run owns the bar and the toast, so the outcome of a sheet it superseded is not told. */
  const reportSheetRun = (run: number, outcome: FulfillmentToastState) => {
    if (run === runCount.current) {
      reportLabelRun(outcome);
    }
  };

  const fail = (err: unknown) => {
    setRun(null);
    showError(describeFailure(err, GENERIC_ERROR));
  };

  /** A refused print names the printer, because that is the thing the picker has to go and look at. */
  const reportPrint = (outcome: PrintOutcome, label: string, orderCount: number) => {
    if (outcome.ok) {
      finish(label, orderCount);
      return;
    }
    settle(showError(PrintFailureNotice.textOf(outcome)));
  };

  /**
   * One toast for the parcels of a click — label and PACKING sheet each — and the orders whose PACKING sheet
   * could not go, named so the picker prints it by hand from View Orders.
   */
  const reportParcelPrint = (results: ParcelPrintOutcome[], label: string) => {
    const refused = sheetOutcomesOf(results).find((outcome): outcome is PrintFailure => !outcome.ok);
    if (refused) {
      settle(showError(PrintFailureNotice.textOf(refused)));
      return;
    }
    const withoutSheet = results.filter((result) => !result.sheetSent).map((result) => result.orderId);
    const notes = [packingNotPrintedNote(withoutSheet, 'View Orders')].filter(Boolean);
    if (notes.length > 0) {
      settle(showWarning(`${label} complete — ${results.length} orders. ${notes.join(' ')}`));
      return;
    }
    finish(label, results.length);
  };

  const outboundRunningRef = useRef(false);

  const bulkOutbound = async () => {
    if (outboundRunningRef.current || selectedBlocked || selectedIds.length === 0) {
      return;
    }
    outboundRunningRef.current = true;
    setBusy(true);
    setBulkResult(null);
    start('Bulk Outbound', selectedIds.length, RELOADS);
    try {
      // A refused batch still answers 200 — refusals are data here, not a transport error.
      const result = await client.bulkOutboundOrders({ orderIds: selectedIds });
      if (!Array.isArray(result?.applied) || !Array.isArray(result?.skipped) || !Array.isArray(result?.failed)) {
        throw new Error('Incomplete bulk outcome');
      }
      const outcome: BulkOutboundResponse = result;
      const returnedIds = [...outcome.applied, ...outcome.skipped, ...outcome.failed].map(({ orderId }) => orderId);
      if (
        returnedIds.length !== selectedIds.length ||
        new Set(returnedIds).size !== returnedIds.length ||
        returnedIds.some((id) => !selectedIds.includes(id))
      ) {
        throw new Error('Inconsistent bulk outcome');
      }
      setBulkResult(outcome);
      const summary = formatBulkResult(
        'Bulk Outbound',
        outcome.applied.length,
        outcome.skipped.map(({ orderId, reason }) => ({ item: `Order ${orderId}`, reason })),
        outcome.failed.map(({ orderId, reason }) => ({ item: `Order ${orderId}`, reason })),
      );
      if (summary.tone === 'error') {
        FulfillmentSound.failure();
      } else if (summary.tone === 'warning') {
        FulfillmentSound.warning();
      } else {
        FulfillmentSound.success();
      }
      reportLabelRun(summary);
      setSelected([]);
      setReloadToken((token) => token + 1);
    } catch {
      FulfillmentSound.failure();
      setRun(null);
      showError(UNCONFIRMED_BULK_RESULT);
    } finally {
      outboundRunningRef.current = false;
      setBusy(false);
    }
  };

  /** One order at a time so the bar means something; an order with no label is reported rather than faked. */
  const collectLabels = async (orderIds: number[]): Promise<CarrierLabelBatch> => {
    const batch: CarrierLabelBatch = { labels: [], drawn: [], missing: [], failed: [] };
    for (const orderId of orderIds) {
      try {
        const payload = await client.getPrintPayload(orderId);
        const trackingNumber = trackingNumberOf(payload, orderId);
        const drawn = drawnLabelOf(orderId, payload);
        const referencePending = drawnLabelRefusalOf(orderId, payload);
        if (referencePending) {
          batch.missing.push({ orderId, reason: referencePending });
        } else if (drawn) {
          // The sheet behind a Deleo-family sticker is the PACKING sheet View Orders prints; read both for one job.
          try {
            drawn.invoice = await client.getPackingInvoice(orderId);
          } catch {
            // The sticker prints alone; the print toast names the order whose sheet to print by hand.
          }
          batch.drawn.push(drawn);
        } else if (trackingNumber === null) {
          batch.missing.push({ orderId, reason: NOT_OUTBOUNDED });
        } else {
          try {
            batch.labels.push({ orderId, trackingNumber, url: await client.getLabelUrl(orderId, trackingNumber) });
          } catch (err) {
            if (isAxiosError(err) && err.response?.status === 404) {
              batch.missing.push({ orderId, reason: NO_CARRIER_LABEL });
            } else {
              batch.failed!.push({ orderId, reason: describeFailure(err, GENERIC_ERROR) });
            }
          }
        }
      } catch (err) {
        batch.failed!.push({ orderId, reason: describeFailure(err, GENERIC_ERROR) });
      }
      setRun((prev) => (prev ? { ...prev, done: prev.done + 1 } : prev));
    }
    return batch;
  };

  /**
   * The sheet is the feedback once it opens — its rows say what each order has — so a run that only prepared
   * labels raises no toast on top of it; a read that failed is still said aloud, and a run that opens nothing is told.
   */
  const settleLabelRun = (label: string, batch: CarrierLabelBatch) => {
    const outcome = labelRunOutcome(label, batch);
    if (!opensLabelSheet(batch)) {
      reportLabelRun(outcome);
      return;
    }
    setLabels({ batch, run: runCount.current });
    // A clean run says nothing twice; a run with an order left out or a read that failed is still told.
    if (outcome.tone === 'success') {
      setRun(null);
    } else {
      reportLabelRun(outcome);
    }
  };

  /**
   * One print at a time from this screen: `busy` disables the buttons only after the next render, and with no
   * "print again?" question a fast double press would otherwise put the same paper through twice.
   */
  const printRunningRef = useRef(false);

  /** One click, one print: the sheet opens printing, not waiting for a second press [SSD-7207, 10/14]. */
  const printLabels = async (label: string, orderIds: number[]) => {
    if (printRunningRef.current) {
      return;
    }
    printRunningRef.current = true;
    setBusy(true);
    start(label, orderIds.length, KEEP_SELECTION);
    try {
      settleLabelRun(label, await collectLabels(orderIds));
    } catch (err) {
      fail(err);
    } finally {
      printRunningRef.current = false;
      setBusy(false);
    }
  };

  /** One row, one parcel, printed — the row button used to open the batch list and print nothing at all. */
  const printRowLabel = async (orderId: number) => {
    if (printRunningRef.current) {
      return;
    }
    printRunningRef.current = true;
    setBusy(true);
    start('Print', 1, KEEP_SELECTION);
    try {
      const batch = await collectLabels([orderId]);
      const label = batch.labels[0];
      if (!label) {
        // A Deleo-family sticker is drawn on the sheet, which prints it on open; an order with no label has no sheet.
        settleLabelRun('Print Labels', batch);
        return;
      }
      // The sheet behind the label is the PACKING sheet View Orders prints, read with the pdf so the two go out together [SSD-7207].
      const [bundle, invoice] = await Promise.all([
        client.getLabelBundle([orderId]),
        client.getPackingInvoice(orderId).catch(() => null),
      ]);
      const parcel: ArchivedParcel = { orderId, trackingNumber: label.trackingNumber, pdf: bundle.pdf, invoice };
      reportParcelPrint(await printParcels(printer, [parcel]), 'Print');
    } catch (err) {
      // The archive holds no pdf it can merge for this parcel; the sheet's Open label link still reaches the carrier's file.
      if (archiveHasNothing(err)) {
        settle(showWarning(`Order ${orderId} — ${NO_CARRIER_LABEL}`));
      } else {
        fail(err);
      }
    } finally {
      printRunningRef.current = false;
      setBusy(false);
    }
  };

  /** The click's whole paperwork through the one dispatcher, as one document: every parcel with its PACKING sheets. */
  const printAllPaperwork = (parcels: Parcel[]): Promise<ParcelPrintOutcome[]> => printPaperwork(printer, parcels);

  /**
   * The label sheet's run holds the screen's printer until it settles, even after the sheet is closed, so no other
   * Print puts the same paper through meanwhile. A run that starts while another print holds it still prints.
   */
  const holdPrinter = async (run: () => Promise<boolean>): Promise<boolean> => {
    const holds = !printRunningRef.current;
    if (holds) {
      printRunningRef.current = true;
      setBusy(true);
    }
    try {
      return await run();
    } finally {
      if (holds) {
        printRunningRef.current = false;
        setBusy(false);
      }
    }
  };

  /**
   * The walk printed on the press, with no preview to press Print in a second time [10/16]. It is read on the
   * click, so a walk another tab already picked is not printed again.
   */
  const printPickList = async () => {
    // The orders as they were ticked at the press, for the read, the job and the report alike.
    const orderIds = selectedIds;
    if (orderIds.length === 0 || printRunningRef.current) {
      return;
    }
    printRunningRef.current = true;
    setBusy(true);
    start('Print Pick Locations', orderIds.length, KEEP_SELECTION);
    try {
      const fresh = (await client.getPickList(orderIds)).picks;
      if (fresh.length === 0) {
        settle(showWarning('Nothing left to pick — the selection has already moved on', undefined, AUTO_DISMISS));
        return;
      }
      let html: string;
      try {
        html = PickListSheet.htmlOf(fresh, new Date());
      } catch {
        settle(
          showWarning(
            'Nothing on a shelf to pick — the sample set and unshelved lines are not on the sheet',
            undefined,
            AUTO_DISMISS,
          ),
        );
        return;
      }
      const outcome = await printer.dispatch({
        surface: 'pickList',
        jobKey: `pickList:${orderIds.join(',')}`,
        role: 'label',
        format: 'html',
        html,
      });
      // An order not on the sheet is named rather than counted as printed: moved on, or nothing on a shelf.
      const onSheet = new Set(PickListSheet.onSheet(fresh).map((pick) => pick.orderId));
      const inFresh = new Set(fresh.map((pick) => pick.orderId));
      const movedOn = orderIds.filter((orderId) => !inFresh.has(orderId));
      const offShelf = orderIds.filter((orderId) => inFresh.has(orderId) && !onSheet.has(orderId));
      // The lines left off an order that is on the sheet; an order named as not on the sheet is not counted again.
      const leftOff = PickListSheet.leftOff(fresh.filter((pick) => onSheet.has(pick.orderId)));
      const leftOffLines = [
        leftOff.unshelved > 0 ? `${plural(leftOff.unshelved, 'line')} with no shelf` : '',
        leftOff.sample > 0 ? `the sample set (${plural(leftOff.sample, 'line')}, picked from its own shelf)` : '',
      ].filter(Boolean);
      if (outcome.ok && movedOn.length + offShelf.length + leftOffLines.length > 0) {
        const notes = [
          movedOn.length > 0 ? `Not on the sheet (already moved on): order ${movedOn.join(', ')}.` : '',
          offShelf.length > 0 ? `Not on the sheet (nothing on a shelf to pick): order ${offShelf.join(', ')}.` : '',
          leftOffLines.length > 0 ? `Lines left off the sheet: ${leftOffLines.join(', ')}.` : '',
        ].filter(Boolean);
        settle(
          showWarning(
            `Print Pick Locations complete — ${orderIds.length - movedOn.length - offShelf.length} orders. ${notes.join(' ')}`,
          ),
        );
        return;
      }
      reportPrint(outcome, 'Print Pick Locations', orderIds.length);
    } catch (err) {
      fail(err);
    } finally {
      printRunningRef.current = false;
      setBusy(false);
    }
  };

  /**
   * The queue's count is a snapshot from the list read; a thread just read or written is the newer truth —
   * unless something started after it answered first: a queue reload, or a later read or post of that thread.
   */
  const claimCommentCount = (orderId: number): CommentCountClaim => {
    const revision = (commentCountRevision.current.get(orderId) ?? 0) + 1;
    commentCountRevision.current.set(orderId, revision);
    return { orderId, revision, generation: queueGeneration.current };
  };

  const syncCommentCount = (claim: CommentCountClaim, commentCount: number) => {
    if (
      claim.generation !== queueGeneration.current ||
      claim.revision !== commentCountRevision.current.get(claim.orderId)
    ) {
      return;
    }
    setOrders((prev) => prev.map((order) => (order.orderId === claim.orderId ? { ...order, commentCount } : order)));
  };

  const openThread = async (orderId: number) => {
    if (openComments === orderId) {
      setOpenComments(null);
      openThreadRef.current = null;
      return;
    }
    setOpenComments(orderId);
    openThreadRef.current = orderId;
    setComments([]);
    const claim = claimCommentCount(orderId);
    try {
      const loaded = (await client.listOrderComments(orderId)).comments;
      syncCommentCount(claim, loaded.length);
      // A slow thread must not land under the row the picker opened next.
      if (openThreadRef.current === orderId) {
        setComments(loaded);
      }
    } catch (err) {
      showError(describeFailure(err, GENERIC_ERROR));
    }
  };

  const postComment = async (orderId: number, text: string) => {
    if (commentInFlightRef.current) {
      throw new Error('A comment is already being saved.');
    }
    commentInFlightRef.current = true;
    const fingerprint = `${orderId}:${text}`;
    if (commentKey.current?.fingerprint !== fingerprint) {
      commentKey.current = { fingerprint, idempotencyKey: uuidv4() };
    }
    const claim = claimCommentCount(orderId);
    try {
      const posted = await client.appendOrderComment(orderId, {
        text,
        idempotencyKey: commentKey.current.idempotencyKey,
      });
      commentKey.current = null;
      syncCommentCount(claim, posted.comments.length);
      if (openThreadRef.current !== orderId) {
        return;
      }
      setComments(posted.comments);
      showSuccess('Comment added.');
    } catch (err) {
      if (openThreadRef.current === orderId) {
        showError(describeFailure(err, GENERIC_ERROR));
      }
      // The panel keeps the draft only if it hears the refusal.
      throw err;
    } finally {
      commentInFlightRef.current = false;
    }
  };

  return (
    <div className="wms-wireframe ff-ultimate wms-ready-to-outbound">
      {/* One feedback slot for the screen, the same component every other fulfillment screen renders [T03]. */}
      {!labels && <FulfillmentToastHost toasts={toasts} onDismiss={dismissToast} onExpire={expireToast} />}

      {bulkResult && (
        <details className="rto-result" role="group" aria-label="Bulk Outbound result" open={left(bulkResult) > 0}>
          <summary>
            Bulk Outbound — {bulkResult.applied.length} outbounded · {bulkResult.skipped.length} already shipped ·{' '}
            {bulkResult.failed.length} refused
          </summary>
          {/* Every order the run did not ship says why, in the server's own words [D-q]. */}
          <ul className="mb-0">
            {bulkResult.skipped.map((order) => (
              <li key={`skipped-${order.orderId}`}>
                Order {order.orderId} — already shipped: {order.reason}
              </li>
            ))}
            {bulkResult.failed.map((order) => (
              <li key={`failed-${order.orderId}`}>
                Order {order.orderId} — {order.reason || order.violations.join(' · ')}
              </li>
            ))}
          </ul>
        </details>
      )}

      <div className="ptitle">
        <h2>WMS - Ready to be Outbounded</h2>
      </div>
      <div className="psub">
        <h3>Ready to be Outbounded Orders</h3>
        <span className="sp" />
        {/* Locked while a run is in flight: a reload empties the queue the run is still working through. */}
        <button
          type="button"
          className="btn btn-blue"
          disabled={actionBusy}
          onClick={() => setReloadToken((token) => token + 1)}
        >
          <RefreshIcon />
          Refresh
        </button>
      </div>

      {/* The queue still lists every order; only the live shop check behind each row is late [SSD-6939]. */}
      {liveStatusDegraded && (
        <div className="rto-degraded" role="status">
          Live WooCommerce status is delayed; some order statuses may be out of date.
        </div>
      )}

      <div className="viewtabs">
        {TABS.map((entry) => (
          <button
            type="button"
            key={entry.key}
            className={entry.key === tab ? 'vtab on' : 'vtab'}
            disabled={actionBusy}
            onClick={() => {
              if (entry.key !== tab) {
                setSelected([]);
                setTab(entry.key);
              }
            }}
          >
            {entry.label} <span className="n">({counts[entry.key]})</span>
          </button>
        ))}
      </div>

      <div className="bulkbar">
        <label className="selectall">
          <input type="checkbox" aria-label="Select all" checked={allVisibleSelected} onChange={toggleAll} /> Select all
        </label>
        {/* Select all skips what the server would refuse, so the bar says how many that is [SSD-6939 19-3]. */}
        <span
          className="split"
          title={
            split.blocked > 0
              ? `Cannot be outbounded yet (${blockedReasons.join(', ')}); these orders are left out of Select all.`
              : undefined
          }
        >
          {split.outboundable} outboundable · {split.blocked} not
        </span>
        <button
          type="button"
          className="btn btn-line btn-sm"
          disabled={busy || selected.length === 0}
          onClick={() => void printPickList()}
        >
          <PrintIcon />
          Print Pick Locations ({selected.length} orders · {selectedUnits(orders, selected)} items)
        </button>
        <button
          type="button"
          className="btn btn-line btn-sm"
          disabled={busy || selected.length === 0}
          title="Prints only orders that already have a waybill; an order with no waybill booked yet gets one from Create Carrier Order on Order Detail."
          onClick={() => void printLabels('Bulk Print Labels', selectedIds)}
        >
          <PrintIcon />
          Bulk Print Labels ({selected.length} orders)
        </button>
        <button
          type="button"
          className="btn btn-green btn-sm"
          disabled={busy || selected.length === 0 || selectedBlocked}
          onClick={() => void bulkOutbound()}
        >
          <BoxIcon />
          Bulk Outbound ({selected.length} orders)
        </button>
        {carrierBlocked.length > 0 && (
          <span className="blocked-note">
            {carrierBlocked.length} order(s) blocked: carrier label missing or unverified. Create/check the carrier
            label on Order Detail, then refresh.
          </span>
        )}
        <span className="cnt">{selected.length} selected</span>
      </div>

      {run && (
        <div className="progressrow">
          <div
            className="pbar"
            role="progressbar"
            aria-label={progressLabel}
            aria-valuenow={percent}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="fill" style={{ width: `${percent}%` }} />
          </div>
          <span className="pbar-label">{progressLabel}</span>
          {runDone && (
            <button
              type="button"
              className="btn btn-line btn-sm"
              aria-label="Close the finished run"
              onClick={() => dismissToast(run.outcomeToastId!)}
            >
              Close
            </button>
          )}
        </div>
      )}

      {orders.some((order) =>
        order.lines.some((line) => line.pickLocation && countingShelves.has(line.pickLocation)),
      ) && (
        <p className="counting-legend">
          <span className="locpill counting">Shelf</span> A dashed shelf is being counted in a stock audit. Pick as
          usual; the count there may change while you pick.
        </p>
      )}

      {/* The table scrolls on its own, so a wide row never drags the whole page sideways [SSD-6939 19-10]. */}
      <div className="tbl-scroll">
        <table className="tbl">
          {/* Fixed columns: a long product name wraps inside its cell and never widens the column (ultimate design). */}
          <colgroup>
            <col className="col-check" />
            <col className="col-id" />
            <col className="col-date" />
            <col className="col-customer" />
            <col className="col-total" />
            <col className="col-items" />
            <col className="col-locs" />
            <col className="col-comments" />
            <col className="col-print" />
          </colgroup>
          <thead>
            <tr>
              <CheckCell as="th" style={{ width: '34px' }}>
                <input
                  type="checkbox"
                  aria-label="Select all orders"
                  checked={allVisibleSelected}
                  onChange={toggleAll}
                />
              </CheckCell>
              <th>Order ID</th>
              <th>Order Date</th>
              <th>Customer</th>
              <th>Total Items</th>
              <th>Ready Item Details</th>
              <th>Pick Locations</th>
              <th>Comments</th>
              <th>Print</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((order, index) => (
              <Fragment key={order.orderId}>
                <tr
                  className={[
                    index % 2 === 1 ? 'striped' : '',
                    order.isMarketing ? 'row-mkt' : '',
                    order.hasJit && !order.isMarketing ? 'row-jit' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <CheckCell title={describeExclusion(order.violations ?? [])}>
                    {/* A box the picker cannot tick has to say why, or the row reads as simply broken [T04]. */}
                    <input
                      type="checkbox"
                      aria-label={`Select order ${order.orderId}`}
                      checked={selected.includes(order.orderId)}
                      disabled={(order.violations ?? []).length > 0}
                      title={describeExclusion(order.violations ?? [])}
                      onChange={() => toggle(order.orderId)}
                    />
                  </CheckCell>
                  <td>
                    {/* The number, then its badges as whole capsules that wrap under it, flush left. Bootstrap's
                        text-nowrap on this cell won over the stylesheet (!important) and pushed them into Order Date. */}
                    <div className="oid-cell">
                      <Link to={ROUTES.fulfillment.orderDetail(order.orderId)} className="oid">
                        {order.orderId}
                      </Link>
                      {order.isMarketing && <span className="mkt-badge">MKT</span>}
                      {/* hasJit is a sourcing route, not a state of arrival — calling it "Fully Inbounded" put it
                          next to "Awaiting inbound" on the same row and left the operator unable to read either. */}
                      {order.hasJit && !order.isMarketing && <span className="jit-badge">JIT</span>}
                      {/* Some of the goods are still on their way, so the picker is not sent for a whole box. */}
                      {order.inboundState === 'PARTIALLY_INBOUNDED' && <span className="viobadge">Partly in</span>}
                      {violationBadges(order.violations ?? []).map((violation) => (
                        <span className="viobadge" key={violation} title={violation}>
                          {violation}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td>{formatOrderDate(order.orderDate)}</td>
                  <td>
                    {order.customerName ?? MISSING}
                    {order.isMarketing && <div className="pic">PIC: {order.pic || MISSING}</div>}
                  </td>
                  <td>
                    <span className="cntbadge cb-total">{unitsOf(order)}</span>
                  </td>
                  <td>
                    {order.lines.map((line) => (
                      <div className="item-line" key={line.lineNo}>
                        <span className="skupill">{line.sku}</span>
                        <span className="pname">{line.productName}</span>
                        <span className="qtypill">×{line.qty}</span>
                      </div>
                    ))}
                  </td>
                  <td>
                    {order.lines.map((line) => (
                      <div className="locline" key={line.lineNo}>
                        {/* WH#00 holds the stock but is no shelf to walk to, so the picker sees no location for it [SSD-7436]. */}
                        {pickerLocation(line.pickLocation) ? (
                          <span
                            className={countingShelves.has(line.pickLocation ?? '') ? 'locpill counting' : 'locpill'}
                            title={
                              countingShelves.has(line.pickLocation ?? '')
                                ? 'Being counted in a stock audit — pick as usual'
                                : undefined
                            }
                          >
                            {line.pickLocation}
                          </span>
                        ) : line.pickLocation ? (
                          <span className="locnone" aria-label={`No shelf location for ${line.sku}`}>
                            {MISSING}
                          </span>
                        ) : (
                          <span className="locpill pending">No shelf yet</span>
                        )}
                      </div>
                    ))}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="cmtbtn"
                      aria-label={`Comments for order ${order.orderId}`}
                      onClick={() => void openThread(order.orderId)}
                    >
                      <CommentIcon />
                      {order.commentCount ? <span className="badge-n">{order.commentCount}</span> : null}
                      {order.unreadCommentCount ? (
                        <span
                          className="badge-unread"
                          title={`${order.unreadCommentCount} unread since you last opened the comments hub`}
                        >
                          {order.unreadCommentCount} new
                        </span>
                      ) : null}
                    </button>
                  </td>
                  <td>
                    <button
                      type="button"
                      className="printbtn"
                      aria-label={`Print order ${order.orderId}`}
                      disabled={busy}
                      onClick={() => void printRowLabel(order.orderId)}
                    >
                      <PrintIcon />
                    </button>
                  </td>
                </tr>
                {openComments === order.orderId && (
                  <tr className="crow">
                    <td colSpan={9}>
                      <RowComments
                        comments={comments}
                        busy={busy}
                        onPost={(text) => postComment(order.orderId, text)}
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {visible.length === 0 && !loading && (
              <tr>
                <td className="emptyrow" colSpan={9}>
                  No orders are ready to outbound.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <p className="foundtxt">
        {loading ? 'Loading…' : `Found ${visible.length} order(s) with items ready for outbound`}
      </p>

      {nextCursor != null && (
        <button type="button" className="btn btn-gray btn-sm" disabled={busy} onClick={() => void loadMore()}>
          Load more
        </button>
      )}

      <div className="howto">
        <b>How to use:</b>
        <ul>
          <li>Click &quot;Refresh&quot; to load the latest orders ready for outbound</li>
          <li>Orders shown here have at least one line item with &quot;INBOUNDED&quot; status</li>
          <li>Click the Order ID link to see full order details and process outbound</li>
          <li>Only items marked as INBOUNDED are ready for outbound processing</li>
          <li>This page uses the dedicated readyToBeOutbonded API for optimized results</li>
        </ul>
      </div>

      {labels && (
        <CarrierLabelModal
          batch={labels.batch}
          onClose={() => setLabels(null)}
          onBundle={(orderIds) => client.getLabelBundle(orderIds)}
          onPackingInvoice={(orderId) => client.getPackingInvoice(orderId).catch(() => null)}
          onPrintAll={printAllPaperwork}
          holdPrinter={holdPrinter}
          onResult={(outcome) => reportSheetRun(labels.run, outcome)}
          notifications={<FulfillmentToastHost toasts={toasts} onDismiss={dismissToast} onExpire={expireToast} />}
        />
      )}
    </div>
  );
}
